// backoffice.settings.location.users (legacy LocationUsersIndex): the people who sign in to this location's back office.
// Search, "Invite User", the users table (Name with initials and a "You" badge, Email, Phone, Role, Last Login, a
// menu with Change Role and Remove), Pending Invitations (Email, Invited By, Pre-assigned Role, Sent, Expires, a menu
// with Resend and Cancel) and the Invite User dialog. Identity is cloud-authoritative: on the node these need internet.
// Lane V's side-by-side (round 14): legacy centres the page in its 908 px settings block (`ui-settings-block`, 36 under
// the header), the search filling the row beside Invite User, and lists only the location's own users (its API reads
// `location_user`), so an account-wide owner is not a row; with none, "No users assigned to this location" and an
// "Invite Your First User" link stand where the table would.
import { useMemo, useState, type ReactNode } from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import { DropdownMenu } from 'radix-ui';
import { EllipsisVertical } from 'lucide-react';
import {
  Banner, Button, ChipIndicator, DataTable, DeleteDialog, Dialog, EmptyState, LoadingState, SECTION_HEADING, SearchField, SelectField, TextField, UiNoDataLine, legacyFieldsSearchMatches, useToast,
} from '@radish/ui-web';
import { PermissionToggles } from './UserRolesPage.tsx';
import { useActiveLocation } from '../../app/location.tsx';
import { initialsOf } from '../../session/session.ts';
import { avatarFillOf } from './avatarFill.ts';
import { ListHeader, LoadError } from '../../shell/page.tsx';
import { EMAIL_PATTERN } from '../../lib/email.ts';
import {
  relativeTime, useCancelInvite, useChangeMemberRole, useInviteUser, useInvites, useMembers, useRemoveMember, useResendInvite, useRoles,
  type Invite, type Member,
} from './data.ts';
import { DELETE_PICTURE } from '../../shell/delete-picture.ts';

const ITEM = 'flex h-9 cursor-pointer items-center px-4 outline-none data-[highlighted]:bg-menu-item-hover data-[disabled]:cursor-default data-[disabled]:opacity-50';

export function RowMenu({ label, children, contentClassName = 'min-w-40 py-1' }: { readonly label: string; readonly children: ReactNode; readonly contentClassName?: string }) {
  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger aria-label={label} className="inline-flex size-9 items-center justify-center rounded-full text-text-2 hover:bg-gray-50">
        <EllipsisVertical size={18} aria-hidden />
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content align="end" sideOffset={4} className={`z-[3000] rounded-q border border-line bg-menu text-[14px] text-menu-ink-body shadow-lg dark:border-transparent dark:shadow-none ${contentClassName}`}>
          {children}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}

export function MenuItem({ onSelect, disabled, negative, children }: { readonly onSelect: () => void; readonly disabled?: boolean; readonly negative?: boolean; readonly children: ReactNode }) {
  return (
    <DropdownMenu.Item disabled={disabled ?? false} onSelect={onSelect} className={`${ITEM} ${negative === true ? 'text-negative' : ''}`}>
      {children}
    </DropdownMenu.Item>
  );
}

export function ConfirmDialog({ open, title, message, action, busy, onConfirm, onClose }: {
  readonly open: boolean; readonly title: string; readonly message: string; readonly action: string; readonly busy: boolean;
  readonly onConfirm: () => void; readonly onClose: () => void;
}) {
  return (
    <Dialog
      open={open}
      onOpenChange={(o) => { if (!o) onClose(); }}
      title={title}
      testId="confirm-dialog"
      footer={<><Button variant="outline" onClick={onClose}>Cancel</Button><Button variant="negative" loading={busy} onClick={onConfirm}>{action}</Button></>}
    >
      <p className="text-[14px] text-text-2">{message}</p>
    </Dialog>
  );
}

export function UsersPage() {
  const location = useActiveLocation();
  const toast = useToast();
  const members = useMembers(location.locationId);
  const invites = useInvites(location.locationId);
  const resend = useResendInvite(location.locationId);
  const cancel = useCancelInvite(location.locationId);
  const remove = useRemoveMember(location.locationId);
  const [term, setTerm] = useState('');
  const [inviting, setInviting] = useState(false);
  const [changing, setChanging] = useState<Member | null>(null);
  const [removing, setRemoving] = useState<Member | null>(null);
  const [cancelling, setCancelling] = useState<Invite | null>(null);
  const [link, setLink] = useState<{ readonly email: string; readonly url: string } | null>(null);
  const me = location.session.view.user.userId;
  const now = Date.now();

  const located = useMemo(() => (members.data?.members ?? []).filter((m) => m.scope === 'location'), [members.data]);
  const rows = useMemo(() => located.filter((m) => legacyFieldsSearchMatches(term, [m.name])), [located, term]);

  const memberColumns = useMemo<ColumnDef<Member, never>[]>(() => [
    {
      id: 'name', header: 'Name', accessorFn: (m) => m.name.toLowerCase() as never,
      cell: (c) => (
        <span className="flex items-center gap-2">
          <span aria-hidden data-testid="users.avatar" className={`inline-flex size-7 flex-none items-center justify-center rounded-full text-xs font-medium ${avatarFillOf(c.row.original.name).className}`}>{initialsOf(c.row.original.name)}</span>
          {c.row.original.name}
          {c.row.original.userId === me ? <ChipIndicator tone="neutral">You</ChipIndicator> : null}
        </span>
      ),
    },
    { id: 'email', header: 'Email', accessorFn: (m) => m.email as never, cell: (c) => c.row.original.email },
    { id: 'phone', header: 'Phone', enableSorting: false, cell: (c) => c.row.original.phone ?? '-' },
    { id: 'role', header: 'Role', enableSorting: false, cell: (c) => <ChipIndicator tone="active">{c.row.original.roleName}</ChipIndicator> },
    {
      id: 'lastLogin', header: 'Last Login', accessorFn: (m) => (m.lastLoginAt ?? '') as never,
      cell: (c) => {
        const at = c.row.original.lastLoginAt;
        return at === null ? <span className="text-text-3">Never</span> : <span title={new Date(at).toLocaleString('en-US')}>{relativeTime(at, now)}</span>;
      },
    },
    {
      id: 'actions', header: () => <span className="sr-only">Actions</span>, enableSorting: false, meta: { align: 'right', width: '60px' },
      cell: (c) => {
        const m = c.row.original;
        const locked = m.userId === me || m.scope === 'account';
        return (
          <RowMenu label={`Actions for ${m.name}`}>
            <MenuItem disabled={locked} onSelect={() => setChanging(m)}>Change Role</MenuItem>
            <MenuItem disabled={locked} negative onSelect={() => setRemoving(m)}>Remove</MenuItem>
          </RowMenu>
        );
      },
    },
  ], [me, now]);

  const inviteColumns = useMemo<ColumnDef<Invite, never>[]>(() => [
    { id: 'email', header: 'Email', accessorFn: (i) => i.email as never, cell: (c) => c.row.original.email },
    { id: 'invitedBy', header: 'Invited By', enableSorting: false, cell: (c) => c.row.original.invitedBy ?? '-' },
    { id: 'role', header: 'Pre-assigned Role', enableSorting: false, cell: (c) => c.row.original.roleName },
    { id: 'sent', header: 'Sent', accessorFn: (i) => i.sentAt as never, cell: (c) => relativeTime(c.row.original.sentAt, now) },
    {
      id: 'expires', header: 'Expires', accessorFn: (i) => i.expiresAt as never,
      cell: (c) => (c.row.original.expired ? <ChipIndicator tone="negative">Expired</ChipIndicator> : relativeTime(c.row.original.expiresAt, now)),
    },
    {
      id: 'actions', header: () => <span className="sr-only">Actions</span>, enableSorting: false, meta: { align: 'right', width: '80px' },
      cell: (c) => {
        const i = c.row.original;
        return (
          <RowMenu label={`Actions for the invitation to ${i.email}`}>
            <MenuItem
              disabled={resend.isPending}
              onSelect={() => resend.mutate({ inviteId: i.inviteId, delivery: 'email' }, { onSuccess: () => toast.success('Invite sent successfully'), onError: (e) => toast.error(e.message) })}
            >
              Resend
            </MenuItem>
            <MenuItem
              disabled={resend.isPending}
              onSelect={() => resend.mutate({ inviteId: i.inviteId, delivery: 'link' }, {
                onSuccess: (sent) => (sent.acceptUrl === undefined ? toast.error('No link came back.') : setLink({ email: i.email, url: sent.acceptUrl })),
                onError: (e) => toast.error(e.message),
              })}
            >
              Copy Invite Link
            </MenuItem>
            <MenuItem negative onSelect={() => setCancelling(i)}>Cancel</MenuItem>
          </RowMenu>
        );
      },
    },
  ], [now, resend, toast]);

  const pending = invites.data?.invites ?? [];
  return (
    // Legacy's settings page top (36 px, as User Roles) and SettingsHeader: the search 20 px from Invite User, 24 px under.
    <div data-testid="users-page" className="mx-auto w-full max-w-[940px] px-4 pb-4 pt-9 max-[1024px]:pt-6">
      <div className="pb-2" data-testid="users-header">
        <ListHeader
          settings
          start={<div className="min-w-0 flex-1"><SearchField value={term} onValueChange={setTerm} placeholder="Search users..." className="min-[600px]:w-full!" /></div>}
          end={<Button onClick={() => setInviting(true)} data-testid="invite-user">Invite User</Button>}
        />
      </div>
      {members.isPending ? <LoadingState /> : members.isLoadingError ? <LoadError error={members.error} /> : located.length === 0 ? (
        // Legacy UiNoData with its button label (UiNoDataLine): the line's text, then the underlined link kept on one line, with
        // no break between the text's last word and the link, so at 600 "location" goes down with it as legacy's (B1482).
        <EmptyState uiNoDataPhone
          testId="users-empty"
          illustration="/assets/missing-states/light/missing_state_employees.svg" darkIllustration="/assets/missing-states/dark/missing_state_employees_dark.svg"
          title="No users assigned to this location"
          description={<UiNoDataLine text="Invite users to give them access to this location" link={{ label: 'Invite Your First User', onClick: () => setInviting(true) }} />}
        />
      ) : (
        <DataTable
          caption="Users"
          testId="users-table"
          columns={memberColumns}
          data={rows}
          getRowId={(m) => m.userId}
          initialSort={[{ id: 'name', desc: false }]}
          empty={<EmptyState uiNoDataPhone illustration="/assets/missing-states/light/missing_state_employees.svg" darkIllustration="/assets/missing-states/dark/missing_state_employees_dark.svg" title="No users assigned to this location" description="Invite users to give them access to this location" />}
        />
      )}
      {pending.length > 0 ? (
        // Legacy LocationUsersIndex's UiSectionHeading (q-mt-lg q-mb-sm): 27.5 under the "Displaying" line, 8 over the table (B1339).
        <section className="mt-6" data-testid="pending-invitations">
          <h2 className={`mb-2 ${SECTION_HEADING}`}>Pending Invitations</h2>
          <DataTable caption="Pending Invitations" columns={inviteColumns} data={pending} getRowId={(i) => i.inviteId} initialSort={[{ id: 'sent', desc: true }]} />
        </section>
      ) : null}
      <InviteDialog locationId={location.locationId} open={inviting} onClose={() => setInviting(false)} onLink={(email, url) => setLink({ email, url })} />
      <InviteLinkDialog link={link} onClose={() => setLink(null)} />
      <ChangeRoleDialog locationId={location.locationId} member={changing} onClose={() => setChanging(null)} />
      {/* Legacy deleteDialog (UiDeleteDialog): its picture, the sentence, Cancel and Delete; the toasts are the API's. */}
      <DeleteDialog
        open={removing !== null}
        onOpenChange={(o) => { if (!o) setRemoving(null); }}
        title="Remove from Location"
        model="user"
        {...DELETE_PICTURE}
        message={`Are you sure you want to remove ${removing?.name ?? ''} from this location? They will lose access immediately.`}
        busy={remove.isPending}
        onConfirm={() => {
          if (removing !== null) remove.mutate(removing.userId, { onSuccess: () => { setRemoving(null); toast.success('User removed successfully from location'); }, onError: (e) => toast.error(e.message) });
        }}
      />
      <DeleteDialog
        open={cancelling !== null}
        onOpenChange={(o) => { if (!o) setCancelling(null); }}
        title="Cancel Invitation"
        model="invitation"
        {...DELETE_PICTURE}
        message={`Are you sure you want to cancel the invitation to ${cancelling?.email ?? ''}?`}
        busy={cancel.isPending}
        onConfirm={() => {
          if (cancelling !== null) cancel.mutate(cancelling.inviteId, { onSuccess: () => { setCancelling(null); toast.success('Invite deleted successfully'); }, onError: (e) => toast.error(e.message) });
        }}
      />
    </div>
  );
}

function useRoleOptions(locationId: string) {
  const roles = useRoles(locationId);
  return (roles.data?.roles ?? []).map((r) => ({ value: r.roleId as string, label: r.name }));
}

/**
 * The accept link of an invitation sent with delivery `link`: shown once, to copy and pass on. Opening it a second time
 * means making a new link (the old one stops working).
 */
export function InviteLinkDialog({ link, onClose }: { readonly link: { readonly email: string; readonly url: string } | null; readonly onClose: () => void }) {
  const toast = useToast();
  const copy = async () => {
    if (link === null) return;
    try {
      await navigator.clipboard.writeText(link.url);
      toast.success('Invite link copied');
    } catch {
      toast.warning('Copy the link from the field.');
    }
  };
  return (
    <Dialog
      open={link !== null}
      onOpenChange={(o) => { if (!o) onClose(); }}
      title="Invite Link"
      testId="invite-link-dialog"
      footer={<><Button variant="outline" onClick={onClose}>Done</Button><Button onClick={() => void copy()}>Copy Link</Button></>}
    >
      <div className="flex flex-col gap-4">
        <p className="text-[14px] text-text-2">
          Send this link to {link?.email} yourself. It works once and expires in seven days, and it is shown only now: making another link stops this one working.
        </p>
        <TextField label="Invite link" value={link?.url ?? ''} onValueChange={() => undefined} readOnly data-testid="invite-link" />
      </div>
    </Dialog>
  );
}

/**
 * Legacy LocationUsersIndex's Invite User dialog. The staff account pages reuse it for their own invite (legacy
 * InviteUser.vue, not yet walked), keeping their button label and no role line until that screen is re-baselined.
 */
export function InviteDialog({ locationId, open, onClose, onLink, sendLabel = 'Send Invitation', roleHint = true }: {
  readonly locationId: string; readonly open: boolean; readonly onClose: () => void; readonly onLink: (email: string, url: string) => void;
  readonly sendLabel?: string; readonly roleHint?: boolean;
}) {
  const toast = useToast();
  const invite = useInviteUser(locationId);
  const options = useRoleOptions(locationId);
  const [email, setEmail] = useState('');
  const [roleId, setRoleId] = useState<string | null>(null);
  const [errors, setErrors] = useState<{ email?: string; role?: string }>({});
  const close = () => {
    setEmail('');
    setRoleId(null);
    setErrors({});
    onClose();
  };
  const send = (delivery: 'email' | 'link') => {
    const trimmed = email.trim();
    const next = {
      ...(trimmed === '' ? { email: 'Email address is required' } : !EMAIL_PATTERN.test(trimmed) ? { email: 'Please enter a valid email address' } : {}),
      ...(roleId === null ? { role: 'Please select a role' } : {}),
    };
    setErrors(next);
    if (Object.keys(next).length > 0 || roleId === null) return;
    invite.mutate({ email: trimmed, roleId, delivery }, {
      onSuccess: (sent) => {
        close();
        if (delivery === 'email') toast.success('Invite sent successfully');
        else if (sent.acceptUrl !== undefined) onLink(trimmed, sent.acceptUrl);
      },
      // Legacy toasts the API's refusal ("The user is already invited to this location") and keeps the dialog open (B1055).
      onError: (e) => toast.error(e.message),
    });
  };
  return (
    <Dialog
      open={open}
      onOpenChange={(o) => { if (!o) close(); }}
      title="Invite User"
      phoneFooterFill
      testId="invite-dialog"
      footer={
        <>
          <Button variant="outline" onClick={close}>Cancel</Button>
          <Button variant="outline" onClick={() => send('link')} loading={invite.isPending && invite.variables.delivery === 'link'} data-testid="copy-invite-link">Copy Invite Link</Button>
          <Button onClick={() => send('email')} loading={invite.isPending && invite.variables.delivery === 'email'}>{sendLabel}</Button>
        </>
      }
    >
      {/* Legacy LocationUsersIndex (B1474): the description is tw-text-sm tw-text-gray-500 (14 px on a 20 px line, #6b7280 in
          both themes) with q-mb-md under it; the role is a dense outlined q-select (40 px, clearable) q-mb-sm under the
          email; the hint is tw-text-xs (a 16 px line) with q-mt-xs over it and q-mb-sm under it. The account pages pass
          roleHint={false} and keep the earlier layout until legacy InviteUser.vue is walked. */}
      <form className={roleHint ? 'flex flex-col' : 'flex flex-col gap-4'} onSubmit={(e) => { e.preventDefault(); send('email'); }}>
        <p className={roleHint ? 'mb-4 text-[14px] leading-5 text-[#6b7280]' : 'text-[14px] text-text-2'} data-testid="invite-description">
          Send an invitation email to add a new user to this location. They will receive an email with a link to create their account or connect their existing account.
          Or copy the invite link and send it to them yourself.
        </p>
        <TextField label="Email Address" type="email" value={email} onValueChange={setEmail} error={errors.email} autoComplete="off" />
        <div className={roleHint ? 'mt-2' : undefined}>
          <SelectField
            label="Role" value={roleId} options={options} onValueChange={setRoleId} error={errors.role} testId="invite-role"
            dense={roleHint} {...(roleHint ? { onClear: () => setRoleId(null) } : {})}
          />
          {/* Legacy's line under the role (tw-text-xs tw-text-gray-400 q-mt-xs: Tailwind 3's gray-400, #9ca3af, in light and
              dark alike). Token `invite-hint-ink`: legacy's #9ca3af on request and in dark, darkened to 4.8:1 under AA. */}
          {roleHint ? <p className="mt-1 mb-2 text-[12px] leading-4 text-invite-hint-ink" data-testid="invite-role-hint">The selected role will be applied when the user accepts the invite.</p> : null}
        </div>
        <button type="submit" hidden aria-hidden tabIndex={-1} />
      </form>
    </Dialog>
  );
}

/**
 * Legacy UserPermissionDialog ("Assign User Role"), the same dialog wherever a user's role is changed: the user named,
 * Select Role on their current role, the chosen role's permissions previewed read-only, legacy's info banner, and
 * Save (off until a role is chosen); the toast is the API's.
 */
/** B748 (PRM-32): no legacy copy, as legacy allowed the change; worded like legacy's refusal to remove the only admin. */
const LAST_ADMIN_ROLE = 'Cannot change the role of the only admin of this location.';

export function ChangeRoleDialog({ locationId, member, onClose }: { readonly locationId: string; readonly member: Member | null; readonly onClose: () => void }) {
  const toast = useToast();
  const change = useChangeMemberRole(locationId);
  const roles = useRoles(locationId);
  const options = useRoleOptions(locationId);
  const [roleId, setRoleId] = useState<string | null>(null);
  const current = roleId ?? member?.roleId ?? null;
  const selected = (roles.data?.roles ?? []).find((r) => r.roleId === current);
  const close = () => {
    setRoleId(null);
    onClose();
  };
  return (
    <Dialog
      open={member !== null}
      onOpenChange={(o) => { if (!o) close(); }}
      title="Assign User Role"
      phoneFooterFill
      testId="change-role-dialog"
      footer={
        <>
          <Button variant="outline" onClick={close}>Cancel</Button>
          <Button
            loading={change.isPending}
            disabled={current === null}
            onClick={() => {
              if (member === null || current === null) return;
              change.mutate({ userId: member.userId, roleId: current }, {
                onSuccess: () => { close(); toast.success('User role assigned successfully.'); },
                // B748: the server refuses moving the location's last Super Admin to another role; the shared sentence speaks of removing.
                onError: (e) => toast.error(e.code === 'identity.last_admin' ? LAST_ADMIN_ROLE : e.message),
              });
            }}
          >
            Save
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <p className="text-[14px] font-medium text-text-1" data-testid="assign-role-to">Assign a role to <strong>{member?.name}</strong></p>
        <SelectField label="Select Role" value={current} options={options} onValueChange={setRoleId} testId="change-role-select" />
        {selected === undefined ? null : (
          <div className="flex flex-col">
            {/* Legacy UserPermissionDialog's UiSectionHeading (q-mt-md q-mb-sm): 16 px under the select (the parent's gap), 8 over the toggles (B1339). */}
            <h3 className={`mb-2 ${SECTION_HEADING}`}>Role Permissions (Preview)</h3>
            <PermissionToggles locationId={locationId} granted={new Set(selected.permissions)} readOnly />
          </div>
        )}
        <Banner tone="info">The selected role controls which backoffice pages this user can access. Changes take effect on their next login.</Banner>
      </div>
    </Dialog>
  );
}
